import { useQuery } from '@apollo/client';
import { RouteComponentProps } from '@reach/router';
import { graphql, navigate, useStaticQuery } from 'gatsby';
import { endsWith, includes } from 'ramda';
import React, { FC, ReactNode, useEffect } from 'react';
import styled, { ThemeProvider } from 'styled-components';

import DashboardHeader from '@ui/organisms/DashboardHeader';
import PageContainer from '@ui/organisms/PageContainer';
import TermsOfServiceDialog from '@ui/organisms/TermsOfServiceDialog';

import useGradient from '@hooks/useGradient';
import { useMemberValidation } from '@hooks/useMemberValidation';

import { keysToCamel } from '@utils/butter';

import { useAuth } from '@providers/AuthProvider';

import { ConsentType } from '@data/consent';

import Footer from '@components/WSFooter';

import IS_ACCEPTANCE_CURRENT from '@graphql/queries/isAcceptanceCurrent.query.gql';

import { getThemeForFlow } from '@theme/FlowThemes';
import theme from '@theme/FolxTheme';

import { IMemberHomeStepPageContent } from './MemberHomeStep/MemberHomeStep.component';
import {
	appointments,
	billing,
	community,
	home,
	labs,
	messages,
} from './MemberHomeStep/QuickLinks';

interface DashboardRouteProps extends RouteComponentProps {
	children?: ReactNode;
}

export const StyledPageContainer = styled(PageContainer)<{
	showGradient?: boolean;
	showWhiteBackground?: boolean;
}>`
	min-height: 100vh;
	background: ${({ showGradient = false, showWhiteBackground = false }) =>
		showGradient ? false : showWhiteBackground ? theme.colors.white : '#f8f7f5'};
	${({ showGradient = false }) => useGradient(showGradient)}
`;

export const DashboardContainer = styled.div<{ removePadding: boolean }>`
	align-items: center;
	/* background: ${theme.colors.white}; */
	display: flex;
	flex-flow: column;
	padding-top: ${theme.spacing.md};
	padding-bottom: ${theme.spacing.xxxl};
	padding-left: ${({ removePadding }) => !removePadding && theme.spacing.md};
	padding-right: ${({ removePadding }) => !removePadding && theme.spacing.md};
	min-height: 100%;
	width: 100%;
	max-width: 68rem;
	overflow-x: hidden;

	@media ${theme.responsive.breakpoints.tablet} {
		padding-left: 0;
		padding-right: 0;
		overflow-x: visible;
	}
`;

const DashboardRoute: FC<DashboardRouteProps> = ({ location, children }) => {
	const { pageContent } = useStaticQuery(graphql`
		{
			pageContent: butterPage(slug: { eq: "member-home-step" }) {
				home_header
				billing_header
				messages_header
				appointments_header
				labs_header
				innerspace_header
			}
		}
	`);
	const {
		homeHeader,
		billingHeader,
		messagesHeader,
		appointmentsHeader,
		labsHeader,
		innerspaceHeader,
	}: IMemberHomeStepPageContent = keysToCamel(pageContent) as IMemberHomeStepPageContent;

	const { loggedIn, initialized } = useAuth();

	const { validateMember, memberMetadata, loaded: memberValidationLoaded } = useMemberValidation();

	const { loading: tosLoading, data: tosData } = useQuery<{ isAcceptanceCurrent: boolean }>(
		IS_ACCEPTANCE_CURRENT,
		{
			fetchPolicy: 'no-cache',
			variables: {
				consentType: ConsentType.TERMS_OF_SERVICE,
			},
		}
	);

	useEffect(() => {
		if (memberValidationLoaded) {
			validateMember();
		}
	}, [memberValidationLoaded]);

	if (initialized && !loggedIn) {
		navigate('/app/sign_in', {
			state: {
				redirectTo: (location?.state?.redirectTo || location?.pathname) ?? '/',
			},
		});

		return <div />;
	}

	const showGradient =
		endsWith('/member/', location?.pathname ?? '') || endsWith('/member', location?.pathname ?? '');

	const showWhiteBackground = includes('/cancel', location?.pathname ?? '');

	const removePadding =
		endsWith('/get_care/', location?.pathname ?? '') ||
		endsWith('/get_care', location?.pathname ?? '');

	const navItems = [
		home({ header: homeHeader }),
		billing({ header: billingHeader }),
		messages({ header: messagesHeader }),
		appointments({ header: appointmentsHeader }),
		labs({ header: labsHeader }),
		community({ header: innerspaceHeader }),
	];

	return (
		<ThemeProvider theme={getThemeForFlow('dashboard')}>
			<StyledPageContainer
				showGradient={showGradient}
				showWhiteBackground={showWhiteBackground}
				header={<DashboardHeader currentPath={location?.pathname} navItems={navItems} />}
				footer={<Footer />}>
				{!tosLoading &&
					!tosData?.isAcceptanceCurrent &&
					memberValidationLoaded &&
					!memberMetadata?.membership?.isMembershipUnpaid && <TermsOfServiceDialog />}
				<DashboardContainer removePadding={removePadding}>{children}</DashboardContainer>
			</StyledPageContainer>
		</ThemeProvider>
	);
};

export default DashboardRoute;
